import { PageHero, SectionHeader } from "@/components/Section";
import useReveal from "@/hooks/useReveal";
import { Link } from "react-router-dom";
import {
  Landmark,
  GraduationCap,
  Handshake,
  Church as ChurchIcon,
  BookOpenCheck,
  Archive,
  Flame,
  MapPin,
  ArrowRight,
  Camera,
  ScrollText,
} from "lucide-react";

// Only verified/known milestones are shown as confirmed history.
// Cards labeled "From the Archives" are gentle placeholders inviting the
// church's records team to submit verified dates, names, and photos.
const timeline = [
  {
    era: "Late 1800s",
    title: "Church Established",
    body:
      "In the aftermath of Emancipation, the congregation is organized to serve African American families in the rural White Station area of Shelby County — becoming one of the oldest Black churches in East Memphis.",
    verified: true,
  },
  {
    era: "Early 1900s",
    title: "Partnership with the Patterson School",
    body:
      "The church supports the Patterson School — one of the first schools serving African American children in the White Station community before integration. Churches often provided meeting space, land, and community support for education during segregation.",
    verified: true,
  },
  {
    era: "From the Archives",
    title: "Founding Deed & Land Purchase",
    body:
      "Original property records, deed transfers, and stories of the land itself. If your family has this history, we'd love to include it.",
    placeholder: true,
  },
  {
    era: "1949",
    title: "A Refuge for a Neighboring Congregation",
    body:
      "Historical accounts record that another local church held worship services at New Philadelphia while constructing its own sanctuary — reflecting our long-standing role as a community anchor and safe harbor.",
    verified: true,
  },
  {
    era: "From the Archives",
    title: "Building Milestones & Restorations",
    body:
      "Every historic church has weathered storms — fires, rebuilds, additions, and expansions. When those records surface, they'll live here as a testimony to God's faithfulness.",
    placeholder: true,
  },
  {
    era: "From the Archives",
    title: "Radio & Community Ministry",
    body:
      "If New Philadelphia's voice reached the wider city through radio broadcasts or community programs, we want to honor those seasons here.",
    placeholder: true,
  },
  {
    era: "From the Archives",
    title: "Pastors Through the Years",
    body:
      "The names, portraits, and stories of each senior servant who has stewarded this pulpit. A complete pastoral succession is being compiled.",
    placeholder: true,
  },
  {
    era: "Modern Era",
    title: "Rev. Dr. Eric L. Blake",
    body:
      "Former Senior Pastor Rev. Dr. Eric L. Blake carried the congregation forward in the missionary Baptist tradition — anchored to the Word and committed to Memphis.",
    verified: true,
  },
  {
    era: "Today",
    title: "Pastor Darren M. Little — Where Tradition Meets Transformation",
    body:
      "Pastor Darren M. Little serves as our Senior Pastor at 533 S. Mendenhall Road, carrying the torch of faith into a new generation.",
    verified: true,
  },
];

const heritage = [
  {
    icon: GraduationCap,
    title: "Patterson School Legacy",
    body:
      "Historically connected to one of the earliest schools for African American children in White Station — a testament to our commitment to education.",
  },
  {
    icon: Handshake,
    title: "A House for the Community",
    body:
      "For generations, New Philadelphia has hosted neighborhood meetings, offered mutual aid, and even sheltered other congregations while their buildings were being built.",
  },
  {
    icon: ChurchIcon,
    title: "Missionary Baptist Tradition",
    body:
      "Rooted in the rich African American Missionary Baptist heritage of Memphis — preaching the whole counsel of God with fervor, order, and love.",
  },
];

const significance = [
  "One of the oldest African American congregations in the White Station area.",
  "Played a role in supporting education for Black children during segregation.",
  "Has remained an active place of worship and community service for well over a century.",
  "A beacon of faith, family, and freedom in East Memphis for generations.",
];

export default function Legacy() {
  useReveal();

  return (
    <div data-testid="legacy-page">
      <PageHero
        eyebrow="Our Legacy"
        title="A house God has preserved for over a century."
        subtitle={`"Ask for the ancient paths, ask where the good way is, and walk in it, and you will find rest for your souls." — Jeremiah 6:16`}
        image="https://images.pexels.com/photos/7520354/pexels-photo-7520354.jpeg?auto=compress&cs=tinysrgb&w=2000"
      />

      {/* Post-Emancipation opening */}
      <section className="bg-white py-24">
        <div className="container-npbc grid lg:grid-cols-[1fr_1.05fr] gap-14 items-center">
          <div data-reveal>
            <div className="eyebrow">Rooted in Freedom</div>
            <h2 className="mt-3 heading-serif text-5xl leading-[1.05]">
              Born out of Emancipation.<br />Raised on faith.
            </h2>
            <p className="mt-6 text-npbc-gray leading-relaxed text-[17px]">
              In the years following the Emancipation of African Americans, formerly enslaved families
              across the South began gathering in brush arbors, one-room chapels, and modest sanctuaries
              to worship God freely — often for the very first time. <span className="font-semibold text-npbc-black">New Philadelphia Missionary Baptist Church</span> —
              affectionately known as <span className="font-semibold text-npbc-black">NPMBC</span> —
              was born from that holy moment. Planted in the late 1800s in the rural White Station community
              east of Memphis, it stood as both a house of worship and a lifeline of faith, education,
              and refuge during the era of segregation.
            </p>
            <p className="mt-4 text-npbc-gray leading-relaxed text-[17px]">
              From those earliest days this was more than a Sunday gathering — it was a school, a shelter,
              a courtroom of prayer, and a place of hope. Long before public services were extended to
              Black residents, this congregation stood as a center of education, civic life, and mutual aid.
            </p>
          </div>
          <div data-reveal className="relative">
            <div className="absolute -inset-3 rounded-3xl bg-gradient-to-br from-npbc-red/10 to-npbc-gold/20 -rotate-1" />
            <img
              src="https://customer-assets-eiarnc6j.emergentagent.net/job_strange-cartwright-8/artifacts/gz83939m_709226385_122123167863216091_9060692169340108500_n.jpg"
              alt="Inside New Philadelphia Missionary Baptist Church sanctuary"
              className="relative rounded-3xl w-full h-[560px] object-cover shadow-2xl"
              loading="lazy"
            />
            <div className="absolute -bottom-6 -right-6 hidden md:block glass rounded-2xl px-5 py-4 shadow-xl max-w-[240px]">
              <div className="text-[10px] font-semibold uppercase tracking-[0.24em] text-npbc-red">
                Since the late 1800s
              </div>
              <div className="heading-serif text-xl mt-1 leading-tight">
                Faith, family, and freedom on Mendenhall Road.
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Heritage pillars */}
      <section className="bg-npbc-black text-white py-24">
        <div className="container-npbc">
          <div data-reveal>
            <SectionHeader
              eyebrow={<span className="!text-npbc-gold">Heritage Pillars</span>}
              title={<span className="text-white">Sacred ground. Steadfast people.</span>}
            />
          </div>
          <div className="mt-14 grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
            {heritage.map((h, i) => (
              <div
                key={h.title}
                data-reveal
                style={{ transitionDelay: `${i * 60}ms` }}
                className="relative rounded-2xl border border-white/10 bg-white/[0.03] p-7 hover:border-npbc-gold/50 transition"
              >
                <div className="h-11 w-11 rounded-2xl bg-npbc-gold/20 text-npbc-gold grid place-items-center">
                  <h.icon className="h-5 w-5" />
                </div>
                <h3 className="mt-5 heading-serif text-2xl leading-snug">{h.title}</h3>
                <p className="mt-2 text-white/70 leading-relaxed text-sm">{h.body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Historically significant */}
      <section className="bg-npbc-cream py-24">
        <div className="container-npbc grid lg:grid-cols-[1fr_1.1fr] gap-14 items-center">
          <div data-reveal className="relative">
            <div className="absolute -inset-3 rounded-3xl bg-gradient-to-br from-npbc-red/10 to-npbc-gold/20 -rotate-1" />
            <img
              src="https://images.pexels.com/photos/7520347/pexels-photo-7520347.jpeg?auto=compress&cs=tinysrgb&w=1400"
              alt="Historic sanctuary interior"
              className="relative rounded-3xl w-full h-[520px] object-cover shadow-2xl"
              loading="lazy"
            />
          </div>
          <div data-reveal>
            <div className="eyebrow">Historically Significant</div>
            <h2 className="mt-3 heading-serif text-5xl leading-[1.05]">
              A witness to the faithfulness of God.
            </h2>
            <p className="mt-5 text-npbc-gray leading-relaxed text-[17px]">
              New Philadelphia Missionary Baptist Church is more than a congregation — it is a living testimony
              of God's steadfast love among African American families in East Memphis, across generations.
            </p>
            <ul className="mt-8 space-y-4">
              {significance.map((s) => (
                <li key={s} className="flex items-start gap-3">
                  <span className="mt-1 h-6 w-6 shrink-0 rounded-full bg-npbc-red text-white grid place-items-center text-xs font-bold">
                    ✓
                  </span>
                  <span className="text-npbc-black/85 leading-relaxed">{s}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </section>

      {/* Timeline — verified + placeholders */}
      <section className="bg-white py-24">
        <div className="container-npbc">
          <div data-reveal>
            <SectionHeader
              eyebrow="A Living Timeline"
              title="Milestones in our story."
              subtitle="Some milestones are confirmed by community memory and historical accounts. Others are marked From the Archives — placeholders inviting church records, deeds, minutes, photographs, and family testimony to fill in the gaps."
            />
          </div>
          <div className="mt-16 relative">
            <div className="absolute left-4 md:left-1/2 md:-translate-x-1/2 top-0 bottom-0 w-px bg-gradient-to-b from-npbc-red via-npbc-gold to-transparent" />
            <div className="space-y-12">
              {timeline.map((t, i) => (
                <div
                  key={`${t.era}-${i}`}
                  data-reveal
                  className={`relative pl-14 md:pl-0 md:grid md:grid-cols-2 md:gap-16 md:items-center ${
                    i % 2 === 1 ? "md:[&>div:first-child]:order-2" : ""
                  }`}
                >
                  <div
                    className={`absolute left-2.5 md:left-1/2 md:-translate-x-1/2 top-2 h-4 w-4 rounded-full ring-4 ring-white shadow ${
                      t.placeholder ? "bg-npbc-gold" : "bg-npbc-red"
                    }`}
                  />
                  <div className={`md:pr-10 ${i % 2 === 1 ? "md:text-right md:pl-10 md:pr-0" : ""}`}>
                    <div
                      className={`text-xs font-semibold tracking-[0.24em] uppercase ${
                        t.placeholder ? "text-npbc-gold" : "text-npbc-red"
                      }`}
                    >
                      {t.era}
                    </div>
                    <h4 className="mt-2 heading-serif text-3xl leading-snug">{t.title}</h4>
                    <p className="mt-2 text-npbc-gray leading-relaxed">{t.body}</p>
                    {t.placeholder && (
                      <div className="mt-3 inline-flex items-center gap-2 text-[11px] font-semibold tracking-wide uppercase text-npbc-gold/80">
                        <Archive className="h-3.5 w-3.5" /> Awaiting archival records
                      </div>
                    )}
                  </div>
                  <div />
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Photographs / archives grid — placeholder for future church photos */}
      <section className="bg-npbc-cream py-24">
        <div className="container-npbc">
          <div data-reveal>
            <SectionHeader
              eyebrow="Historic Photographs"
              title="A living museum, still being written."
              subtitle="Old bulletins, anniversary programs, pastor portraits, sanctuary photos, and community memories — every image tells a piece of the story. As the church's archives are digitized, this gallery will grow."
            />
          </div>
          <div className="mt-14 grid gap-4 sm:grid-cols-2 md:grid-cols-3">
            {Array.from({ length: 6 }).map((_, i) => (
              <div
                key={i}
                data-reveal
                style={{ transitionDelay: `${i * 40}ms` }}
                className="group relative aspect-[4/3] rounded-2xl border border-dashed border-npbc-black/15 bg-white grid place-items-center overflow-hidden hover:border-npbc-red/50 transition"
              >
                <div className="text-center px-6">
                  <div className="mx-auto grid h-12 w-12 place-items-center rounded-full bg-npbc-cream text-npbc-red">
                    <Camera className="h-5 w-5" />
                  </div>
                  <div className="mt-4 text-[10px] font-semibold tracking-[0.24em] uppercase text-npbc-gray/70">
                    Historic Photograph {i + 1}
                  </div>
                  <div className="mt-1 heading-serif text-lg text-npbc-black/85 leading-tight">
                    Coming from the archives
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Contribute to the archives */}
      <section className="bg-npbc-black text-white py-24">
        <div className="container-npbc grid lg:grid-cols-[1.2fr_1fr] gap-12 items-center">
          <div data-reveal>
            <div className="eyebrow !text-npbc-gold">Help Us Preserve the Story</div>
            <h2 className="mt-3 heading-serif text-4xl md:text-5xl leading-[1.05]">
              Do you carry a piece of our history?
            </h2>
            <p className="mt-4 text-white/75 text-lg leading-relaxed">
              If your family, church records, or personal collection holds photographs, deeds, church
              minutes, anniversary books, pastor portraits, or written recollections —
              we would be honored to preserve them here as part of the church's living archive.
            </p>
            <div className="mt-6 flex flex-wrap items-center gap-4 text-sm text-white/70">
              <span className="inline-flex items-center gap-2"><Camera className="h-4 w-4 text-npbc-gold" /> Photographs</span>
              <span className="inline-flex items-center gap-2"><ScrollText className="h-4 w-4 text-npbc-gold" /> Documents</span>
              <span className="inline-flex items-center gap-2"><BookOpenCheck className="h-4 w-4 text-npbc-gold" /> Testimonies</span>
              <span className="inline-flex items-center gap-2"><Flame className="h-4 w-4 text-npbc-gold" /> Family stories</span>
            </div>
          </div>
          <div data-reveal className="flex flex-col gap-3">
            <Link to="/contact" data-testid="legacy-contribute-cta" className="btn-gold w-full justify-center">
              Submit to the Archives <ArrowRight className="h-4 w-4" />
            </Link>
            <Link to="/future" className="btn-outline w-full justify-center">
              Read Our Future <ArrowRight className="h-4 w-4" />
            </Link>
          </div>
        </div>
      </section>

      {/* Closing torch */}
      <section className="bg-white py-24">
        <div className="container-npbc text-center max-w-3xl mx-auto" data-reveal>
          <div className="eyebrow justify-center flex">A Living Legacy</div>
          <h3 className="mt-3 heading-serif text-4xl md:text-5xl leading-tight">
            The torch of faith, still burning bright.
          </h3>
          <p className="mt-4 text-npbc-gray text-[17px] leading-relaxed">
            For more than 150 years, each generation has received the torch of faith from those who came
            before them. Today, we are honored to carry that torch forward, trusting God to write the next
            chapter of New Philadelphia's story.
          </p>
        </div>
      </section>
    </div>
  );
}
